import React, {Fragment} from "react";
import styles from "./HierarchyFilter.module.scss";
import {cloneDeep} from "lodash";
import {Dropdown} from "components/ui/Dropdown";
import areValuesEqual from "react-fast-compare";
import {isAfter, isBefore, isEqual, parse} from "date-fns";
import {usePrevious} from "hooks/usePrevious";
import {FilterDetails} from "helpers/useGetFilterDetails";
import ReviewFeedFilterConfig from "../../types/ReviewFeedFilterConfig";
import ReviewConsolidationFilterConfig from "../../types/ReviewFeedFilterConfig";
import {hideFilterMenu} from "../../reducers/filterMenu";
import {useDispatch} from "react-redux";

export type DatasetFilters = Record<string,
    {
        displayName: string;
        possibleValues: { label: string; value: any; parent: null | string; ancestors: null | string; }[];
    }>;

export type TimePeriodFilter = {
    displayName: string;
    possibleValues: {
        label: string;
        value: { startDate: string; endDate: string };
    }[];
};

export type TimePeriodFilters = {
    Date1?: TimePeriodFilter;
    Date2?: TimePeriodFilter;
    Date3?: TimePeriodFilter;
    Date4?: TimePeriodFilter;
    Date5?: TimePeriodFilter;
    Date6?: TimePeriodFilter;
    Date7?: TimePeriodFilter;
    Date8?: TimePeriodFilter;
    Date9?: TimePeriodFilter;
    Date10?: TimePeriodFilter;
    Yearly?: TimePeriodFilter;
    YearToDate?: TimePeriodFilter;
    Quarterly?: TimePeriodFilter;
    Monthly?: TimePeriodFilter;
    Weekly?: TimePeriodFilter;
};

export type TimePeriod =
    "Date1"
    | "Date2"
    | "Date3"
    | "Date4"
    | "Date5"
    | "Date6"
    | "Date7"
    | "Date8"
    | "Date9"
    | "Date10"
    | "Yearly"
    | "YearToDate"
    | "Quarterly"
    | "Monthly"
    | "Weekly"


export type GeneralFiltersDetails = {
    datasetFilters: DatasetFilters;
    timePeriodFilters: TimePeriodFilters;
};

type ReviewFeedFilterDetails = {
    classificationFilters: {
        [key: string]: {
            displayName: string;
            possibleValues: { label: string; value: string }[];
        };
    };
    reviewFilters: {
        [key: string]: {
            displayName: string;
            possibleValues: { label: string; value: string }[];
        };
    };
    staffFilters: {
        [key: string]: {
            displayName: string;
            possibleValues: { label: string; value: string }[];
        };
    };
    reviewSourceFilters: {
        [key: string]: {
            displayName: string;
            possibleValues: { label: string; value: string }[];
        };
    };
    questionNameFilters: {
        [key: string]: {
            displayName: string;
            possibleValues: { label: string; value: string }[];
        };
    };
};

type ReviewConsolidationFilterDetails = {
    classificationFilters: {
        [key: string]: {
            displayName: string;
            possibleValues: { label: string; value: string }[];
        };
    };
    reviewFilters: {
        [key: string]: {
            displayName: string;
            possibleValues: { label: string; value: string }[];
        };
    };
    staffFilters: {
        [key: string]: {
            displayName: string;
            possibleValues: { label: string; value: string }[];
        };
    };
    reviewSourceFilters: {
        [key: string]: {
            displayName: string;
            possibleValues: { label: string; value: string }[];
        };
    };
    questionNameFilters: {
        [key: string]: {
            displayName: string;
            possibleValues: { label: string; value: string }[];
        };
    };
};
type CommentFiltersDetails = {
    classificationFilters: {
        [key: string]: {
            displayName: string;
            possibleValues: { label: string; value: string }[];
        };
    };
    adjectiveFilters: {
        [key: string]: {
            displayName: string;
            possibleValues: { label: string; value: string }[];
        };
    };
    nounFilters: {
        [key: string]: {
            displayName: string;
            possibleValues: { label: string; value: string }[];
        };
    };
    staffFilters: {
        [key: string]: {
            displayName: string;
            possibleValues: { label: string; value: string }[];
        };
    };
    reviewSourceFilters: {
        [key: string]: {
            displayName: string;
            possibleValues: { label: string; value: string }[];
        };
    };
    questionNameFilters: {
        [key: string]: {
            displayName: string;
            possibleValues: { label: string; value: string }[];
        };
    };
};

export type GeneralFiltersData = {
    errors: unknown[];
    details: GeneralFiltersDetails;
};

type ReviewFiltersData = {
    errors: unknown[];
    details: ReviewFeedFilterDetails;
};

type ReviewConsolidationFiltersData = {
    errors: unknown[];
    details: ReviewConsolidationFilterDetails;
};

type CommentAnalysisFiltersData = {
    errors: unknown[];
    details: CommentFiltersDetails;
};

function HierarchyFilter(props: {
    filters: string;
    noTime?: boolean;
    setFilters(s: string): void;
    projectId: number;
    commentAnalysis?: boolean;
    reviewFeed?: boolean;
    reviewConsolidationFeed?: boolean;
    filterDetails: FilterDetails;
    onlyNouns?: boolean;
    preFilterSubmitCb?: () => void;
    reviewFeedFilterConfig?: ReviewFeedFilterConfig;
    reviewConsolidationFilterConfig?: ReviewConsolidationFilterConfig;
    loading?: boolean;
}): JSX.Element | null {
    const {
        projectId,
        filters,
        setFilters,
        filterDetails,
        commentAnalysis,
        onlyNouns,
        noTime,
        preFilterSubmitCb,
        loading,
        reviewFeedFilterConfig,
        reviewConsolidationFilterConfig
    } = props;

    if (filterDetails.data) {
        return (

            <HierarchyFilterView
                details={filterDetails.data.general}
                reviewFeedFilters={filterDetails.data.review}
                commentAnalysis={commentAnalysis}
                commentAnalysisFilters={filterDetails.data.comment}
                filterString={filters}
                setFilterString={setFilters}
                projectId={projectId}
                onlyNouns={onlyNouns}
                noTime={noTime}
                preFilterSubmitCb={preFilterSubmitCb}
                reviewFeedFilterConfig={reviewFeedFilterConfig}
                reviewConsolidationFeedFilters={reviewConsolidationFilterConfig}

                loading={loading}
            />
        );
    }

    return <Fragment>
        <button
            className="Btn BtnLarge"
            onClick={() => {
                setFilters("reset");
            }}
            style={{
                marginBottom: "5px",
                margin: "auto",
                display: "flex"
            }}
        >
            Reset Filters
        </button>
    </Fragment>;
}

function getFilterParams(
    vals: Record<
        string,
        {
            value: string | { startDate: string; endDate: string };
            label: string;
        } | null
    >,
    keywords: string
): string {
    let pairs = Object.entries(vals)
        .filter(([_, val]) => val != null)
        .map(([key, datum]) => {
            let val: any;
            if (typeof datum!.value === "string" || datum!.value === null) {
                val = datum!.value;
            } else {
                val = `${datum!.value.startDate}__${datum!.value.endDate}`;
            }
            return `${key}::${val}`;
        });

    if (keywords) pairs = pairs.concat(keywords ? `keywords::${keywords}` : "");
    return pairs.join("||");
}

type FilterRecord = Record<
    string,
    {
        value: string | { startDate: string; endDate: string };
        label: string;
    } | null
>;

function deserializeFilterParam(
    filterString: string,
    datasetFilters: DatasetFilters,
    timePeriodFilters: TimePeriodFilters,
    reviewFeedFilters?: ReviewFeedFilterDetails,
    commentAnalysisFilters?: CommentFiltersDetails
): [FilterRecord, string] {
    let values = filterString.split("||").map((f) => f.split("::"));
    const keywords = values.find((x) => x[0] === "keywords");

    let record: FilterRecord = {};

    for (const [key, val] of Object.entries(datasetFilters)) {
        const filteredVal = values.find((x) => x[0].toLowerCase() === key.toLowerCase());

        if (!filteredVal) {
            record[key] = null;
            continue;
        }

        const labelAndVal = val.possibleValues.find((x) => x.value === filteredVal[1]);

        if (!labelAndVal) {
            record[key] = null;
            continue;
        }

        record[key] = {label: labelAndVal.label, value: labelAndVal.value};
    }

    for (const [key, val] of Object.entries(timePeriodFilters)) {
        const filteredVal = values.find((x) => x[0].toLowerCase() === key.toLowerCase());

        if (!filteredVal) {
            record[key] = null;
            continue;
        }

        const labelAndVal = val?.possibleValues.find((x) =>
            areValuesEqual(x.value, convertTimeStringToObj(filteredVal[1]))
        );

        if (!labelAndVal) {
            record[key] = null;
            continue;
        }

        record[key] = {label: labelAndVal.label, value: filteredVal[1]};
    }

    if (reviewFeedFilters) {
        const reviewFilters = [
            reviewFeedFilters.classificationFilters,
            reviewFeedFilters.staffFilters,
            reviewFeedFilters.reviewFilters,
            reviewFeedFilters.reviewSourceFilters
        ].flatMap(obj => {
            if (obj !== null) {
                return Object.entries(obj);
            } else {
                return []
            }
        });

        for (const [key, val] of reviewFilters) {
            const filteredVal = values.find((x) => x[0].toLowerCase() === key.toLowerCase());

            if (!filteredVal) {
                record[key] = null;
                continue;
            }

            const labelAndVal = val.possibleValues.find((x) => x.value === filteredVal[1]);

            if (!labelAndVal) {
                record[key] = null;
                continue;
            }

            record[key] = {label: labelAndVal.label, value: labelAndVal.value};
        }
    }

    if (commentAnalysisFilters) {
        const commentFilters = [
            commentAnalysisFilters.classificationFilters,
            commentAnalysisFilters.staffFilters,
            commentAnalysisFilters.adjectiveFilters,
            commentAnalysisFilters.nounFilters,
            commentAnalysisFilters.reviewSourceFilters
        ].flatMap(obj => {
            if (obj !== null) {
                return Object.entries(obj);
            } else {
                return []
            }
        });

        for (const [key, val] of commentFilters) {
            const filteredVal = values.find((x) => x[0].toLowerCase() === key.toLowerCase());

            if (!filteredVal) {
                record[key] = null;
                continue;
            }

            const labelAndVal = val.possibleValues.find((x) => x.value === filteredVal[1]);

            if (!labelAndVal) {
                record[key] = null;
                continue;
            }

            record[key] = {label: labelAndVal.label, value: labelAndVal.value};
        }
    }

    return [record, keywords ? keywords[1] : ""] as [typeof record, string];
}

function HierarchyFilterView(props: {
    details: GeneralFiltersDetails,
    reviewFeedFilters?: ReviewFeedFilterDetails,
    commentAnalysis?: boolean,
    commentAnalysisFilters?: CommentFiltersDetails,
    projectId: number,
    filterString: string,
    setFilterString(s: string): void,
    onlyNouns?: boolean,
    noTime?: boolean,
    preFilterSubmitCb?: () => void,
    loading?: boolean,
    reviewFeedFilterConfig?: ReviewFeedFilterConfig,
    reviewConsolidationFeedFilters?: ReviewConsolidationFilterConfig
}): JSX.Element | null {
    const {
        filterString,
        setFilterString,
        reviewFeedFilters,
        commentAnalysis,
        commentAnalysisFilters,
        onlyNouns,
        noTime,
        details: {datasetFilters, timePeriodFilters},
        preFilterSubmitCb,
        loading,
        reviewFeedFilterConfig,
        reviewConsolidationFeedFilters
    } = props;

    const [filtersFromServer, keywordsFromServer] = deserializeFilterParam(
        filterString,
        datasetFilters,
        timePeriodFilters,
        reviewFeedFilters,
        commentAnalysisFilters
    );
    const [keywords, setKeywords] = React.useState(keywordsFromServer);
    const [selectedOptions, setSelectedOptions] = React.useState<
        Record<
            string,
            {
                value: string | { startDate: string; endDate: string };
                label: string;
            } | null
        >
    >(filtersFromServer);

    //console.log("selected options", selectedOptions);

    // Reset local state if something else changes the filter string.
    const prevFilterString = usePrevious(filterString);

    React.useEffect(() => {
        if (prevFilterString !== filterString) {
            const [filtersFromServer, keywordsFromServer] = deserializeFilterParam(
                filterString,
                datasetFilters,
                timePeriodFilters,
                reviewFeedFilters,
                commentAnalysisFilters
            );
            setSelectedOptions(filtersFromServer);
            setKeywords(keywordsFromServer);
        }
    }, [prevFilterString, filterString, filtersFromServer, keywordsFromServer, datasetFilters, timePeriodFilters, reviewFeedFilters, commentAnalysisFilters]);

    //console.log("commentAnalysisFilters", commentAnalysisFilters);

    function clearState() {
        let initialDetails: Record<string, { value: string; label: string } | null> = {};

        for (const key of Object.keys(selectedOptions)) {
            initialDetails[key] = null;
        }
        return initialDetails;
    }
    const dispatch = useDispatch();

    const filterParams = getFilterParams(selectedOptions, keywords);
    const submit = () => {
        preFilterSubmitCb && preFilterSubmitCb();
        setFilterString(filterParams);
        dispatch(hideFilterMenu());
    };

    const clearSelections = () => {
        preFilterSubmitCb && preFilterSubmitCb();
        if (!filterString || filterString === "reset") {
            setSelectedOptions(clearState());
            setKeywords("");
        } else {
            setFilterString("reset");
            setKeywords("");
        }
    };

    // const clearSelections = () => {
    //   setFilterString("reset");
    //   setKeywords("");
    // };

    let draft = cloneDeep(datasetFilters);
    let hasFilterValues = false;
    const filterOptions = Object.entries(draft);

    // Remove items from each hierarchy if their ancestor is not selected,
    // if a parent is selected at all.
    for (let i = 0; i < filterOptions.length; i++) {
        // Loop over all ancestors to pick up any matching values
        for (let k = i; k >= 0; k--) {
            if (filterOptions[k - 1]) {
                const ancestorsName = filterOptions[k - 1][0];
                const ancestorsValue = selectedOptions[ancestorsName];
                const possibleValues = filterOptions[i][1].possibleValues;
                if (ancestorsValue) {
                    // Loop backwards so we don't skip entries as we remove things from the list.
                    for (let j = possibleValues.length - 1; j >= 0; j--) {
                        if (possibleValues[j].ancestors) {
                            const ancestorsValues = possibleValues[j].ancestors?.split("||");
                            if (ancestorsValues && ancestorsValues[k - 1]) {
                                const possibleValueForAncestors = ancestorsValues[k - 1];
                                if (possibleValueForAncestors !== ancestorsValue?.value) {
                                    possibleValues.splice(j, 1);
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    // Remove duplicate options for each hierarchy.
    for (const hierarchy of filterOptions) {
        const PossibleValues = hierarchy[1].possibleValues;
        const alreadyEncountered = new Set();
        for (let i = PossibleValues.length - 1; i >= 0; i--) {
            if (alreadyEncountered.has(PossibleValues[i].value)) {
                PossibleValues.splice(i, 1);
            } else {
                alreadyEncountered.add(PossibleValues[i].value);
            }
        }
        if (PossibleValues.length > 1)
            hasFilterValues = true;
    }

    let timePeriodDraft = cloneDeep(timePeriodFilters);

    // let timePeriodDraft = React.useMemo(() => cloneDeep(timePeriodFilters), [
    //   selectedOptions,
    // ]);

    // grab all the possible values
    let yearToDateValue = timePeriodDraft.YearToDate?.possibleValues;
    let quarterlyValues = timePeriodDraft.Quarterly?.possibleValues;
    let monthlyValues = timePeriodDraft.Monthly?.possibleValues;
    let weeklyValues = timePeriodDraft.Weekly?.possibleValues;
    /*
      Custom periods may require a revisit as they will require more thought to avoid conflicts.
      Expected behaviour would be to use either the standard date period options or custom but not a combination.
      Custom periods can be any date range and do not get progressively narrower so conflicts could occur up or downstream.
      At present any date dropdown selection will set the below dropdowns to null.
    */
    //let customPeriodValues = timePeriodDraft.Date1?.possibleValues;

    // if a year is selected go through the downstream date options and remove those that conflict (fall outside of year)
    const year = selectedOptions.Yearly;

    if (yearToDateValue && year != null) {
        removeBy(year as any, yearToDateValue);
    }
    if (quarterlyValues && year != null) {
        removeBy(year as any, quarterlyValues);
    }
    if (monthlyValues && year != null) {
        removeBy(year as any, monthlyValues);
    }
    if (weeklyValues && year != null) {
        removeBy(year as any, weeklyValues);
    }
    // if (customPeriodValues && year != null) {
    //   removeBy(year as any, customPeriodValues);
    // }

    // if year to date is selected go through the downstream date options and remove those that conflict
    const yearToDate = selectedOptions.YearToDate;

    if (quarterlyValues && yearToDate != null) {
        removeBy(yearToDate as any, quarterlyValues);
    }
    if (monthlyValues && yearToDate != null) {
        removeBy(yearToDate as any, monthlyValues);
    }
    if (weeklyValues && yearToDate != null) {
        removeBy(yearToDate as any, weeklyValues);
    }
    // if (customPeriodValues && yearToDate != null) {
    //   removeBy(yearToDate as any, customPeriodValues);
    // }

    // if a quarter is selected go through the downstream date options and remove those that conflict
    const quarter = selectedOptions.Quarterly;

    if (monthlyValues && quarter != null) {
        removeBy(quarter as any, monthlyValues);
    }
    if (weeklyValues && quarter != null) {
        removeBy(quarter as any, weeklyValues);
    }
    // if (customPeriodValues && quarter != null) {
    //   removeBy(quarter as any, customPeriodValues);
    // }

    // if a month is selected go through the downstream date options and remove those that conflict
    const month = selectedOptions.Monthly;

    if (weeklyValues && month != null) {
        removeBy(month as any, weeklyValues);
    }
    // if (customPeriodValues && month != null) {
    //   removeBy(month as any, customPeriodValues);
    // }

    const handleChange = (key: string, index: number) => (value: any) => {
        const downstreamSelections = Object.entries(selectedOptions)
            .slice(index + 1)
            .filter((f: any) => f[0].includes("Hierarchy") || f[0].includes("UniqueID"));

        //console.log("downstream dataset", downstreamSelections);

        const newValues: typeof selectedOptions = {};

        newValues[key] = value;

        // const potentialOptions = filterOptions
        //   .slice(index + 1)
        //   .map((x) => x[1].possibleValues.map((x) => x.value));

        // for (let i = 0; i < downstreamSelections.length; i++) {
        //   const [key, stateObject] = downstreamSelections[i];
        //   console.log("downstream field", key, stateObject);
        //   console.log("potential options", potentialOptions[i]);
        //   if (!potentialOptions[i]?.includes(stateObject?.value)) {
        //     newValues[key] = null;
        //   }
        // }

        for (let i = 0; i < downstreamSelections.length; i++) {
            const [key] = downstreamSelections[i];
            newValues[key] = null;
        }

        setSelectedOptions((prev) => ({...prev, ...newValues}));
    };

    const handleChangeTime = (key: string) => (value: any) => {
        const i = Object.entries(selectedOptions).findIndex(
            (idx) => idx[0].toLowerCase() === key.toLowerCase()
        );
        //console.log("index of selected date option", i);

        const downstreamSelections = Object.entries(selectedOptions)
            .slice(i + 1)
            .filter(
                (f: any) =>
                    f[0] === "YearToDate" ||
                    f[0] === "Quarterly" ||
                    f[0] === "Monthly" ||
                    f[0] === "Weekly" ||
                    f[0].includes("Date")
            );

        const newValues: typeof selectedOptions = {};

        newValues[key] = value;

        for (let i = 0; i < downstreamSelections.length; i++) {
            const [key] = downstreamSelections[i];
            newValues[key] = null;
        }

        //console.log("downstream time period", downstreamSelections);
        //setSelectedOptions((prev) => ({ ...prev, [key]: value }));
        setSelectedOptions((prev) => ({...prev, ...newValues}));
    };

    const handleChangeFilter = (key: string) => (value: any) =>
        setSelectedOptions((prev) => ({...prev, [key]: value}));

    const showKeywordSearch = !!(commentAnalysisFilters || reviewFeedFilters);

    let noFiltersApplied = true;
    if (keywords !== "") noFiltersApplied = false;
    for (const val of Object.values(selectedOptions)) {
        if (val != null) {
            noFiltersApplied = false;
            break;
        }
    }

    return (
        <>
            <div>
                <div className="FilterMenuHeadingFlex">
                    <h3>Filter</h3>
                    {!noFiltersApplied && (
                        <button
                            className="Btn BtnSmall"
                            onClick={clearSelections}
                            style={{marginBottom: "5px"}}
                        >
                            Clear
                        </button>
                    )}
                </div>
                {showKeywordSearch && (
                    <React.Fragment>
                        <h5>Keywords</h5>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                submit();
                            }}
                        >
                            <input
                                className="FormBox EditBox"
                                value={keywords}
                                onChange={(e) => setKeywords(e.target.value)}
                            ></input>
                        </form>
                    </React.Fragment>
                )}
                {hasFilterValues &&
                    <div className="FilterSet">
                        <h5>Dataset</h5>
                        {filterOptions.map(([key, value], i) => (
                            value.possibleValues.length > 1 && <div key={key}>

                                <Dropdown
                                    highlighted={selectedOptions[key] != null}
                                    label={value.displayName}
                                    name={key}
                                    value={selectedOptions[key]}
                                    options={value.possibleValues.map((x) => ({
                                        value: x.value,
                                        label: x.label,
                                    }))}
                                    type="Filter"
                                    onChange={handleChange(key, i)}
                                    placeholder="Please select"
                                />
                            </div>
                        ))}
                </div>}
                {!noTime && (
                    <div className="FilterSet">
                        <h5>Time Period</h5>
                        {Object.entries(timePeriodDraft).map(([key, value], i) => {
                            return (
                                <React.Fragment key={key}>
                                    {value && (
                                        <div className={styles.filters}>
                                            <Dropdown
                                                highlighted={selectedOptions[key] != null}
                                                label={value.displayName}
                                                name={key}
                                                value={selectedOptions[key]}
                                                options={value.possibleValues.map((value) => ({
                                                    label: value.label,
                                                    value: value.value as any,
                                                })).filter(
                                                    // Filter future values
                                                    // Don't like using 'Date' but works for now
                                                    (x) => Date.parse(x.value.startDate) <= Date.now()
                                                ).reverse()}
                                                type="Filter"
                                                onChange={handleChangeTime(key)}
                                                placeholder="Please select"
                                            />
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                )}
                {reviewFeedFilters && (
                    <React.Fragment>
                        {(reviewFeedFilters.reviewSourceFilters && reviewFeedFilterConfig?.filterReviewSourceVisible) &&
                            <div className="FilterSet">
                                <h5>Review Source</h5>
                                {Object.entries(reviewFeedFilters.reviewSourceFilters).map(([key, value]) => {
                                    return (
                                        <div key={key} className={styles.filters}>
                                            <Dropdown
                                                highlighted={selectedOptions[key] != null}
                                                label={value.displayName}
                                                name={key}
                                                value={selectedOptions[key]}
                                                options={value.possibleValues.map((value) => {
                                                    if (value.label !== "") {
                                                        return ({
                                                            label: value.label,
                                                            value: value.value as never,
                                                        });
                                                    } else {
                                                        return ({
                                                            label: "Online Survey",
                                                            value: value.value as never
                                                        })
                                                    }
                                                })}
                                                type="Filter"
                                                onChange={handleChangeFilter(key)}
                                                placeholder="Please select"
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        }
                        {(reviewFeedFilters.questionNameFilters && reviewFeedFilterConfig?.filterQuestionNameVisible) &&
                            <div className="FilterSet">
                                <h5>Question Name</h5>
                                {Object.entries(reviewFeedFilters.questionNameFilters).map(([key, value]) => {
                                    return (
                                        <div key={key} className={styles.filters}>
                                            <Dropdown
                                                highlighted={selectedOptions[key] != null}
                                                label={value.displayName}
                                                name={key}
                                                value={selectedOptions[key]}
                                                options={value.possibleValues.filter(value => value.label !== "").map((value) => {
                                                    return ({
                                                        label: value.label,
                                                        value: value.value as any,
                                                    });
                                                })}
                                                type="Filter"
                                                onChange={handleChangeFilter(key)}
                                                placeholder="Please select"
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        }
                        {(reviewFeedFilters.staffFilters && reviewFeedFilterConfig?.filterStaffVisible) &&
                            <div className="FilterSet">
                                <h5>Staff</h5>
                                {Object.entries(reviewFeedFilters.staffFilters).map(([key, value]) => {
                                    return (
                                        <div key={key} className={styles.filters}>
                                            <Dropdown
                                                highlighted={selectedOptions[key] != null}
                                                label={value.displayName}
                                                name={key}
                                                value={selectedOptions[key]}
                                                options={value.possibleValues.map((value) => ({
                                                    label: value.label,
                                                    value: value.value as any,
                                                }))}
                                                type="Filter"
                                                onChange={handleChangeFilter(key)}
                                                placeholder="Please select"
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        }
                        {(reviewFeedFilters.reviewFilters && reviewFeedFilterConfig?.filterReviewVisible) &&
                            <div className="FilterSet">
                                <h5>Review</h5>
                                {Object.entries(reviewFeedFilters.reviewFilters).map(([key, value]) => (
                                    <div key={key} className={styles.filters}>
                                        <Dropdown
                                            highlighted={selectedOptions[key] != null}
                                            label={value.displayName}
                                            name={key}
                                            value={selectedOptions[key]}
                                            options={value.possibleValues.map((value) => ({
                                                label: value.label,
                                                value: value.value as any,
                                            }))}
                                            type="Filter"
                                            onChange={handleChangeFilter(key)}
                                            placeholder="Please select"
                                        />
                                    </div>
                                ))}
                            </div>
                        }
                        {(reviewFeedFilters.classificationFilters && reviewFeedFilterConfig?.filterClassificationVisible) &&
                            <div className="FilterSet">
                                <h5>Classification</h5>
                                {Object.entries(reviewFeedFilters.classificationFilters).map(([key, value]) => (
                                    <div key={key} className={styles.filters}>
                                        <Dropdown
                                            highlighted={selectedOptions[key] != null}
                                            label={value.displayName}
                                            name={key}
                                            value={selectedOptions[key]}
                                            options={value.possibleValues.map((value) => ({
                                                label: value.label,
                                                value: value.value as any,
                                            }))}
                                            type="Filter"
                                            onChange={handleChangeFilter(key)}
                                            placeholder="Please select"
                                        />
                                    </div>
                                ))}
                            </div>
                        }
                    </React.Fragment>
                )}
                {commentAnalysis && commentAnalysisFilters && (
                    <React.Fragment>
                        <div className="FilterSet">
                            <h5>Reviews</h5>
                            {(commentAnalysisFilters.reviewSourceFilters) &&
                                <div className="FilterSet">
                                    {Object.entries(commentAnalysisFilters.reviewSourceFilters).map(([key, value]) => {
                                        return (
                                            <div key={key} className={styles.filters}>
                                                <Dropdown
                                                    highlighted={selectedOptions[key] != null}
                                                    label={value.displayName}
                                                    name={key}
                                                    value={selectedOptions[key]}
                                                    options={value.possibleValues.map((value) => {
                                                        if (value.label !== "") {
                                                            return ({
                                                                label: value.label,
                                                                value: value.value as never,
                                                            });
                                                        } else {
                                                            return ({
                                                                label: "Online Survey",
                                                                value: value.value as never
                                                            })
                                                        }
                                                    })}
                                                    type="Filter"
                                                    onChange={handleChangeFilter(key)}
                                                    placeholder="Please select"
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            }
                            {(commentAnalysisFilters.questionNameFilters) &&
                                <div className="FilterSet">
                                    <h5>Questions</h5>
                                    {Object.entries(commentAnalysisFilters.questionNameFilters).map(([key, value]) => {
                                        return (
                                            <div key={key} className={styles.filters}>
                                                <Dropdown
                                                    highlighted={selectedOptions[key] != null}
                                                    label={value.displayName}
                                                    name={key}
                                                    value={selectedOptions[key]}
                                                    options={value.possibleValues.filter(value => value.label !== "").map((value) => {
                                                        return ({
                                                            label: value.label,
                                                            value: value.value as any,
                                                        });
                                                    })}
                                                    type="Filter"
                                                    onChange={handleChangeFilter(key)}
                                                    placeholder="Please select"
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            }
                            {(commentAnalysisFilters.staffFilters) &&
                                <div className="FilterSet">
                                    <h5>Staff</h5>
                                    {Object.entries(commentAnalysisFilters.staffFilters).map(([key, value]) => {
                                        return (
                                            <div key={key} className={styles.filters}>
                                                <Dropdown
                                                    highlighted={selectedOptions[key] != null}
                                                    label={value.displayName}
                                                    name={key}
                                                    value={selectedOptions[key]}
                                                    options={value.possibleValues.map((value) => ({
                                                        label: value.label,
                                                        value: value.value as any,
                                                    }))}
                                                    type="Filter"
                                                    onChange={handleChangeFilter(key)}
                                                    placeholder="Please select"
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            }
                            <div className="FilterSet">
                                {(commentAnalysisFilters.classificationFilters) &&
                                    <Fragment>
                                        <h5>Classification</h5>
                                        {Object.entries(commentAnalysisFilters.classificationFilters).map(([key, value]) => (
                                            <div key={key} className={styles.filters}>
                                                <Dropdown
                                                    highlighted={selectedOptions[key] != null}
                                                    label={value.displayName}
                                                    name={key}
                                                    value={selectedOptions[key]}
                                                    options={value.possibleValues.map((value) => ({
                                                        label: value.label,
                                                        value: value.value as any,
                                                    }))}
                                                    type="Filter"
                                                    onChange={handleChangeFilter(key)}
                                                    placeholder="Please select"
                                                />
                                            </div>
                                        ))}
                                    </Fragment>
                                }
                                {(commentAnalysisFilters.nounFilters) &&
                                    <Fragment>
                                        {Object.entries(commentAnalysisFilters.nounFilters).map(([key, value]) => (
                                            <div key={key} className={styles.filters}>
                                                <Dropdown
                                                    highlighted={selectedOptions[key] != null}
                                                    label={value.displayName}
                                                    name={key}
                                                    value={selectedOptions[key]}
                                                    options={value.possibleValues.map((value) => ({
                                                        label: value.label,
                                                        value: value.value as any,
                                                    }))}
                                                    type="Filter"
                                                    onChange={handleChangeFilter(key)}
                                                    placeholder="Please select"
                                                />
                                            </div>
                                        ))}
                                    </Fragment>
                                }
                                {(commentAnalysisFilters.adjectiveFilters) &&
                                    <Fragment>
                                        {Object.entries(commentAnalysisFilters.adjectiveFilters).map(([key, value]) => (
                                            <div key={key} className={styles.filters}>
                                                <Dropdown
                                                    highlighted={selectedOptions[key] != null}
                                                    label={value.displayName}
                                                    name={key}
                                                    value={selectedOptions[key]}
                                                    options={value.possibleValues.map((value) => ({
                                                        label: value.label,
                                                        value: value.value as any,
                                                    }))}
                                                    type="Filter"
                                                    onChange={handleChangeFilter(key)}
                                                    placeholder="Please select"
                                                />
                                            </div>
                                        ))}
                                    </Fragment>
                                }
                            </div>
                        </div>
                    </React.Fragment>
                )}
                <div>
                    <button className="Btn BtnBlue" onClick={submit} style={{width: "100%"}} disabled={loading}>
                        Apply filters
                    </button>
                </div>
            </div>
        </>
    );
}

function removeBy(
    period: { value: { startDate: string; endDate: string } },
    draft: { value: { startDate: string; endDate: string } }[]
) {
    const [periodStart, periodEnd] = [
        parse((period.value as any).startDate as any, "yyyy-MM-dd", new Date()),
        parse((period.value as any).endDate, "yyyy-MM-dd", new Date()),
    ];

    for (let i = draft.length - 1; i >= 0; i--) {
        const [start, end] = [
            parse(draft[i].value.startDate, "yyyy-MM-dd", new Date()),
            parse(draft[i].value.endDate, "yyyy-MM-dd", new Date()),
        ];
        const lowerBound = isAfter(start, periodStart) || isEqual(start, periodStart);
        const upperBound = isBefore(end, periodEnd) || isEqual(end, periodEnd);

        const isInBounds = lowerBound && upperBound;

        if (!isInBounds) {
            draft.splice(i, 1);
        }
    }
}

const convertTimeStringToObj = (s: string) => {
    const tuple = s.split("__");
    if (tuple.length !== 2) return null;

    return {startDate: tuple[0], endDate: tuple[1]};
};
export default HierarchyFilter;
